<template>
  <div
    v-if="props.videoId"
    ref="playerContainer"
    class="video-container"
    :aria-label="`watch video of ${seoAlt}`"
  >
    <video
      :id="`video_${props.videoId}`"
      ref="player"
      class="video-js vjs-main_video"
      controls="true"
      draggable="false"
      preload="auto"
      :poster="`https://s7d1.scene7.com/is/image/BLMDev/${props.videoId}`"
      @play="handleVideoPlay"
      @pause="handleVideoPause"
      @timeupdate="handleTimeUpdate"
    >
      <source
        :src="`https://s7d1.scene7.com/is/content/BLMDev/${props.videoId}.m3u8?packagedStreaming=true`"
        type="application/x-mpegURL"
      >
    </video>
  </div>
</template>

<script setup>
import {
  computed, ref, watch,
} from 'vue';
import { usePageData } from '@composables/usePageData';
import { useCommonProductGetters } from '@composables/useCommonProductGetters';
import { getSeoAlt } from '@utils/getSeoAlt';
import { loadScript } from '@utils/loadScript';
import { loadStylesheet } from '@utils/loadStylesheet';
import { tagVideo } from '../features/analytics/video';

const props = defineProps({
  videoId: {
    type: String,
    required: true,
  },
  isVideoSelected: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { productId } = usePageData();
const { productName, brandName } = useCommonProductGetters({ productId });

// added default duration of the video to avoid NaN in tests
const DEFAULT_DURATION = 60;
const seoAlt = computed(() => getSeoAlt(productName.value, brandName.value));
const player = ref({
  currentTime: 0,
  duration: DEFAULT_DURATION,
});
const playerContainer = ref(null);
const utagData = {
  video_id: [props.videoId],
  product_id: [productId],
  product_name: [productName?.value],
};

const handleVideoPlay = () => {
  tagVideo({
    videoTimeViewed: player.value.currentTime,
    videoDuration: player.value.duration || DEFAULT_DURATION,
    data: utagData,
  }, 'start');
};
const handleVideoPause = () => {
  tagVideo({
    videoTimeViewed: player.value.currentTime,
    videoDuration: player.value.duration || DEFAULT_DURATION,
    data: utagData,
  }, 'pause');
};
const handleTimeUpdate = () => {
  tagVideo({
    videoTimeViewed: player.value.currentTime,
    videoDuration: player.value.duration || DEFAULT_DURATION,
    data: utagData,
  }, 'timeupdate');
};

const setupIntersectionObserver = (videojsPlayer) => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        videojsPlayer.pause();
      }
    });
  });
  observer.observe(videojsPlayer.el());
};

const loadVideoPlayer = async () => {
  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/video.js/8.22.0/video.min.js');
  await loadStylesheet('https://cdnjs.cloudflare.com/ajax/libs/video.js/8.22.0/video-js.min.css');
  const videoContainer = playerContainer.value.querySelectorAll('.vjs-main_video');
  // eslint-disable-next-line no-undef
  const videojsPlayer = videojs(`video_${props.videoId}`);
  setupIntersectionObserver(videojsPlayer);

  if (videoContainer) {
    videoContainer.forEach((v) => v.setAttribute('draggable', 'false'));
  }
};

watch(() => props.isVideoSelected, (isVideoSelected) => {
  if (isVideoSelected) {
    loadVideoPlayer();
  }
});

</script>

<style lang="scss" scoped>
  .video-container {
    width: 100%;
    height: 100%;
  }

  .vjs-main_video {
    width: 100%;
    height: 100%;
    user-select: none;
  }
</style>

<style lang="scss">
  .video-js.vjs-main_video .vjs-big-play-button {
    top: 50%;
    left: 50%;
    margin-left: -1em;
    margin-top: -1em;
    width: 2em;
    height: 2em;
    line-height: 2em;
    border: none;
    border-radius: 50%;
    font-size: 5em;
    background-color: rgba(0, 0, 0, .45);
    color: #fff;
    -webkit-transition: border-color .4s, outline .4s, background-color .4s;
    -moz-transition: border-color .4s, outline .4s, background-color .4s;
    -ms-transition: border-color .4s, outline .4s, background-color .4s;
    -o-transition: border-color .4s, outline .4s, background-color .4s;
    transition: border-color .4s, outline .4s, background-color .4s;
  }

  .video-js.vjs-main_video  .vjs-big-play-button:active,
  .video-js.vjs-main_video  .vjs-big-play-button:focus,
  .video-js.vjs-main_video:active .vjs-big-play-button,
  .video-js.vjs-main_video:hover .vjs-big-play-button {
    background-color: #d52484;
  }
</style>
